import { createLogic } from 'redux-logic';
import { stopSubmit } from 'redux-form';
import moment from 'moment';
import history from '../utils/history';
import {
  GET_SUBSCRIPTIONS_PENDING,
  UPDATE_SUBSCRIPTIONS_PAYMENT_PENDING,
  UPDATE_SUBSCRIPTION_STATUS_PENDING,
  SUBSCRIPTION_PAYMENT_TYPES,
  PATH_SUBSCRIPTIONS,
  CANCEL_SUBSCRIPTION_PENDING
} from '../constants';
import {
  getSubscriptionsPending,
  getSubscriptionsSuccess,
  getSubscriptionsError,
  setSubscriptionsLoaded,
  updateSubscriptionPaymentError,
  updateSubscriptionPaymentSuccess,
  reactivateSubscriptionSuccess,
  reactivateSubscriptionError,
  cancelSubscriptionSuccess,
  cancelSubscriptionError,
  setBannerState,
} from '../actions/AppActions';

export const getSubscriptionsLogic = createLogic({
  type: GET_SUBSCRIPTIONS_PENDING,
  latest: true,
  process({ httpClient, getState }, dispatch, done) {
    const intl = getState().intl.messages;
    dispatch(setSubscriptionsLoaded(false));
    httpClient(dispatch).get('/subscriptions')
      .then(res => {
        const results = res.data.subscriptions.filter(s => moment(s.scheduledOrderDate).isAfter(moment().subtract('18', 'months')));
        dispatch(getSubscriptionsSuccess(results));
        dispatch(setSubscriptionsLoaded(true));
        done();
      })
      .catch(err => {
        const bannerState = {
          data: { type: 'error', message: intl.Errors.Subscriptions.Error_Retrieving }
        };
        dispatch(setBannerState(bannerState));
        dispatch(getSubscriptionsError(err));
        dispatch(setSubscriptionsLoaded(true));
        done();
      });
  }
});

const billingAccountPaymentMethod = ({ accountNumber: account, billingAccountId, organizationId, id } = {}) => ({
  id,
  account,
  billingAccountId,
  organizationId,
});

const creditCardPaymentMethod = ({ token, cardHolderName, expirationMonth, expirationYear, id } = {}) => ({
  card: {
    id,
    cardholderName: cardHolderName,
    expMonth: expirationMonth,
    expYear: expirationYear,
    token,
  }
});

export const updateSubscriptionsPaymentMethodLogic = createLogic({
  type: UPDATE_SUBSCRIPTIONS_PAYMENT_PENDING,
  latest: true,
  process({ httpClient, getState, action: { payload } }, dispatch, done) {
    const { type, paymentMethod: rawPaymentMethod, subscriptionId, subscriptionName, subscriptionStatus, form } = payload;
    const paymentMethod = type === SUBSCRIPTION_PAYMENT_TYPES.CREDIT_CARD
      ? creditCardPaymentMethod(rawPaymentMethod)
      : billingAccountPaymentMethod(rawPaymentMethod);
    httpClient(dispatch).post('/subscription-commands/updatesubscriptionpaymentmethod', { id: subscriptionId, paymentMethod })
      .then(() => {
        dispatch(updateSubscriptionPaymentSuccess());
        dispatch(stopSubmit(form));
        if (subscriptionStatus === 'Active') {
          dispatch(getSubscriptionsPending());
          history.push(PATH_SUBSCRIPTIONS);
          const intl = getState().intl.messages;
          const bannerState = {
            data: {
              type: 'success',
              message: `${subscriptionName}: ${intl.Success.Subscriptions.Payment_Update_Success} ${intl.Success.Subscriptions.Payment_Update_Success_Renewal}`
            }
          };
          dispatch(setBannerState(bannerState));
        }
        done();
      }).catch(error => {
        dispatch(stopSubmit(form));
        dispatch(updateSubscriptionPaymentError(error?.response?.data ?? error));
        history.push(PATH_SUBSCRIPTIONS);
        done();
      });
  }
});

export const updateSubscriptionReactivationLogic = createLogic({
  type: UPDATE_SUBSCRIPTION_STATUS_PENDING,
  latest: true,
  process({ httpClient, getState, action }, dispatch, done) {
    const intl = getState().intl.messages;
    httpClient(dispatch).post('/subscription-commands/reactivatesubscription', action.payload)
      .then(() => {
        dispatch(reactivateSubscriptionSuccess());
        const bannerState = {
          data: { type: 'success', message: intl.Success.Subscriptions.Reactivate_Success }
        };
        dispatch(setBannerState(bannerState));
        dispatch(getSubscriptionsPending());
        done();
      }).catch(error => {
        const bannerState = {
          data: { type: 'error', message: intl.Errors.Subscriptions.Error_Reactivating }
        };
        dispatch(setBannerState(bannerState));
        dispatch(reactivateSubscriptionError(error.response));
        done();
      });
  }
});

export const cancelSubscriptionLogic = createLogic({
  type: CANCEL_SUBSCRIPTION_PENDING,
  latest: true,
  process({ httpClient, action }, dispatch, done) {
    const { reason, note, sub } = action.payload;
    const jsonBody = { reasonCancelled: reason, id: sub._id, explanation: note };
    httpClient(dispatch).post('/subscription-commands/cancelsubscription', jsonBody)
      .then(() => {
        dispatch(cancelSubscriptionSuccess({ message: 'Success! The subscription was canceled.' }));
        done();
      })
      .catch(() => {
        dispatch(cancelSubscriptionError('We\'re sorry. Unable to cancel the subscription. Please try again.'));
        done();
      });
  }
});

export default [
  getSubscriptionsLogic,
  updateSubscriptionsPaymentMethodLogic,
  updateSubscriptionReactivationLogic,
  cancelSubscriptionLogic,
];
